import { useState, useEffect } from 'react';
import { 
  Bot, 
  Settings, 
  MessageCircle, 
  Users, 
  TrendingUp, 
  Search,
  RefreshCw,
  Loader2,
  Power,
  ToggleLeft,
  ToggleRight,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Eye
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/vedvaani`;

export default function AdminVedVaaniPage({ admin }) {
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsHasMore, setLogsHasMore] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [searchUserId, setSearchUserId] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === 'logs') {
      fetchLogs();
    }
  }, [activeTab, logsPage, searchUserId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('dharmbook_admin_token');
      const [settingsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/settings?token=${token}`),
        axios.get(`${API}/admin/stats?token=${token}`)
      ]);
      setSettings(settingsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load VedVaani data');
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    setLogsLoading(true);
    try {
      const token = localStorage.getItem('dharmbook_admin_token');
      const params = new URLSearchParams({
        token,
        page: logsPage,
        limit: 20
      });
      if (searchUserId) {
        params.append('user_id', searchUserId);
      }
      
      const response = await axios.get(`${API}/admin/logs?${params}`);
      setLogs(response.data.logs || []);
      setLogsTotal(response.data.total || 0);
      setLogsHasMore(response.data.has_more || false);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
      toast.error('Failed to load chat logs');
    } finally {
      setLogsLoading(false);
    }
  };

  const updateSettings = async (updates) => {
    setSavingSettings(true);
    try {
      const token = localStorage.getItem('dharmbook_admin_token');
      const params = new URLSearchParams({ token, ...updates });
      await axios.put(`${API}/admin/settings?${params}`);
      
      setSettings(prev => ({ ...prev, ...updates }));
      toast.success('Settings updated successfully');
    } catch (error) {
      console.error('Failed to update settings:', error);
      toast.error('Failed to update settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const toggleEnabled = () => {
    updateSettings({ enabled: !settings?.enabled });
  };

  const updateDailyLimit = (value) => {
    const limit = parseInt(value);
    if (limit >= 1 && limit <= 100) {
      updateSettings({ daily_limit: limit });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center">
            <Bot className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">VedVaani AI Control</h1>
            <p className="text-sm text-gray-500">Manage AI Jyotish chatbot settings</p>
          </div>
        </div>
        <button
          onClick={fetchData}
          className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
        >
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b">
        {[
          { id: 'overview', label: 'Overview', icon: TrendingUp },
          { id: 'settings', label: 'Settings', icon: Settings },
          { id: 'logs', label: 'Chat Logs', icon: MessageCircle }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-purple-600 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Status Card */}
          <div className={`rounded-2xl p-6 ${
            settings?.enabled 
              ? 'bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200' 
              : 'bg-gradient-to-r from-red-50 to-orange-50 border border-red-200'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
                  settings?.enabled ? 'bg-green-500' : 'bg-red-500'
                }`}>
                  <Power className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-800">
                    VedVaani is {settings?.enabled ? 'Active' : 'Disabled'}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {settings?.enabled 
                      ? 'Users can chat with AI Jyotish guide'
                      : 'AI chatbot is currently disabled for all users'
                    }
                  </p>
                </div>
              </div>
              <button
                onClick={toggleEnabled}
                disabled={savingSettings}
                className="flex items-center gap-2"
              >
                {settings?.enabled ? (
                  <ToggleRight className="w-12 h-12 text-green-500" />
                ) : (
                  <ToggleLeft className="w-12 h-12 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-5 shadow-sm border">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-purple-600" />
                </div>
                <span className="text-sm text-gray-500">Today's Messages</span>
              </div>
              <p className="text-3xl font-bold text-gray-800">{stats?.today?.messages || 0}</p>
            </div>
            
            <div className="bg-white rounded-xl p-5 shadow-sm border">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <span className="text-sm text-gray-500">Today's Users</span>
              </div>
              <p className="text-3xl font-bold text-gray-800">{stats?.today?.unique_users || 0}</p>
            </div>
            
            <div className="bg-white rounded-xl p-5 shadow-sm border">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                </div>
                <span className="text-sm text-gray-500">Weekly Messages</span>
              </div>
              <p className="text-3xl font-bold text-gray-800">{stats?.weekly_messages || 0}</p>
            </div>
            
            <div className="bg-white rounded-xl p-5 shadow-sm border">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Bot className="w-5 h-5 text-amber-600" />
                </div>
                <span className="text-sm text-gray-500">Total Messages</span>
              </div>
              <p className="text-3xl font-bold text-gray-800">{stats?.all_time?.total_messages || 0}</p>
            </div>
          </div>

          {/* Quick Settings */}
          <div className="bg-white rounded-xl p-6 shadow-sm border">
            <h3 className="font-semibold text-gray-800 mb-4">Quick Settings</h3>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-700">Daily Message Limit</p>
                <p className="text-sm text-gray-500">Maximum messages per user per day</p>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  value={settings?.daily_limit || 20}
                  onChange={(e) => setSettings(prev => ({ ...prev, daily_limit: parseInt(e.target.value) || 20 }))}
                  min="1"
                  max="100"
                  className="w-20 px-3 py-2 border rounded-lg text-center"
                />
                <button
                  onClick={() => updateDailyLimit(settings?.daily_limit)}
                  disabled={savingSettings}
                  className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50"
                >
                  {savingSettings ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl p-6 shadow-sm border">
            <h3 className="font-semibold text-gray-800 mb-6">VedVaani Settings</h3>
            
            <div className="space-y-6">
              {/* Enable/Disable */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div>
                  <p className="font-medium text-gray-700">Enable VedVaani AI</p>
                  <p className="text-sm text-gray-500">Allow users to access the AI chatbot</p>
                </div>
                <button
                  onClick={toggleEnabled}
                  disabled={savingSettings}
                  className={`relative w-14 h-7 rounded-full transition-colors ${
                    settings?.enabled ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                >
                  <span className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    settings?.enabled ? 'left-8' : 'left-1'
                  }`} />
                </button>
              </div>

              {/* Daily Limit */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div>
                  <p className="font-medium text-gray-700">Daily Message Limit</p>
                  <p className="text-sm text-gray-500">Maximum messages per user per day (1-100)</p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={settings?.daily_limit || 20}
                    onChange={(e) => setSettings(prev => ({ ...prev, daily_limit: parseInt(e.target.value) || 20 }))}
                    min="1"
                    max="100"
                    className="w-24 px-3 py-2 border rounded-lg text-center"
                  />
                  <button
                    onClick={() => updateDailyLimit(settings?.daily_limit)}
                    disabled={savingSettings}
                    className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50"
                  >
                    Update
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Info Card */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">AI Behavior Guidelines</p>
                <p className="text-sm text-amber-700 mt-1">
                  VedVaani is configured to provide only spiritual and astrological guidance. 
                  It will not give medical, legal, financial, or political advice. 
                  Extreme predictions are blocked, and users are encouraged to consult human astrologers.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Logs Tab */}
      {activeTab === 'logs' && (
        <div className="space-y-4">
          {/* Search */}
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by User ID..."
                value={searchUserId}
                onChange={(e) => {
                  setSearchUserId(e.target.value);
                  setLogsPage(1);
                }}
                className="w-full pl-10 pr-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={fetchLogs}
              className="p-2.5 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-xl border"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>

          {/* Logs List */}
          {logsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No chat logs found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {logs.map((log) => (
                <div
                  key={log.id}
                  className="bg-white rounded-xl p-4 shadow-sm border hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => setSelectedLog(log)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <span className="font-medium text-gray-800">{log.user_name || 'Unknown User'}</span>
                      <span className="text-xs text-gray-400 ml-2">{log.user_id}</span>
                    </div>
                    <span className="text-xs text-gray-500">
                      {new Date(log.created_at).toLocaleString('en-IN')}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 line-clamp-2">
                    <span className="text-blue-600 font-medium">User:</span> {log.message}
                  </p>
                  <p className="text-sm text-gray-500 line-clamp-2 mt-1">
                    <span className="text-amber-600 font-medium">VedVaani:</span> {log.response?.substring(0, 100)}...
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {logs.length > 0 && (
            <div className="flex items-center justify-between pt-4">
              <p className="text-sm text-gray-500">
                Showing {((logsPage - 1) * 20) + 1} - {Math.min(logsPage * 20, logsTotal)} of {logsTotal}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setLogsPage(p => Math.max(1, p - 1))}
                  disabled={logsPage === 1}
                  className="p-2 border rounded-lg disabled:opacity-50 hover:bg-gray-50"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setLogsPage(p => p + 1)}
                  disabled={!logsHasMore}
                  className="p-2 border rounded-lg disabled:opacity-50 hover:bg-gray-50"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Log Detail Modal */}
      {selectedLog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-800">Chat Log Details</h3>
                <p className="text-xs text-gray-500">
                  {new Date(selectedLog.created_at).toLocaleString('en-IN')}
                </p>
              </div>
              <button
                onClick={() => setSelectedLog(null)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                ✕
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[60vh] space-y-4">
              <div>
                <p className="text-xs text-gray-500 mb-1">User ({selectedLog.user_name})</p>
                <p className="bg-blue-50 p-3 rounded-xl text-gray-800">{selectedLog.message}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">VedVaani Response</p>
                <div className="bg-amber-50 p-3 rounded-xl text-gray-800 prose prose-sm max-w-none">
                  {selectedLog.response}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
